import type {
  AnalyticsEvent,
  AnalyticsUser,
  BaseAnalyticsProperties,
  IAnalyticsProvider,
} from '@cmoh/shared'
import posthog from 'posthog-js'
import { env } from '@/config/env'

/**
 * PostHog implementation of analytics provider.
 * PostHog is initialized by PostHogProvider in main.tsx — this class only wraps methods.
 */
export class PostHogProvider implements IAnalyticsProvider {
  init(): void {
    // PostHog is initialized by <PostHogProvider> in main.tsx. There is no local
    // state to set up: posthog-js buffers any capture/identify issued before it
    // finishes loading and flushes them once ready.
  }

  identify(user: AnalyticsUser): void {
    if (!env.analytics.enabled) return

    const { id, email, name, organizationId, role, ...rest } = user

    posthog.identify(id, {
      email,
      name,
      organizationId,
      role,
      ...rest,
    })
  }

  track<T extends BaseAnalyticsProperties>(event: AnalyticsEvent<T>): void {
    if (!env.analytics.enabled) return

    posthog.capture(event.event, {
      ...event.properties,
      timestamp: event.timestamp || new Date(),
    })
  }

  setUserProperties(properties: BaseAnalyticsProperties): void {
    if (!env.analytics.enabled) return

    posthog.people.set(properties)
  }

  captureException(error: Error, context?: BaseAnalyticsProperties): void {
    if (!env.analytics.enabled) return

    posthog.capture('exception', {
      error_message: error.message,
      error_name: error.name,
      stack_trace: error.stack,
      ...context,
    })
  }
}
